import { CSSProperties, memo } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import Icon from 'components/Icon';
import VideoPlayer from 'components/VideoPlayer';
import { getChatDocumentPresentation } from 'utils/chatDocument';
import { toSafeHttpUrl } from 'utils/safeUrl';

type TVideoOptions = {
  isAutoPlayVideo: boolean;
  controls: boolean;
  /** タップで全画面再生を開始する（省略時は従来どおり有効） */
  isFullscreenOnclick?: boolean;
};

type TMedia = {
  type: string;
  url: string;
  fileName?: string;
  thumbnail?: string;
  poster?: string;
};

type Props = TMedia & {
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
  onLoad?: () => void;
  videoOptions?: TVideoOptions;
  topOffset?: string | number;
  bottomOffset?: string | number;
  scrollableAncestor?: HTMLElement | null;
};

const MediaView = ({
  type,
  url,
  fileName,
  className = '',
  style,
  onClick,
  onLoad,
  videoOptions = { controls: false, isAutoPlayVideo: true },
  poster,
  thumbnail,
  topOffset,
  bottomOffset,
  scrollableAncestor: propScrollableAncestor,
}: Props) => {
  const scrollableAncestor = propScrollableAncestor || undefined;
  // PDF / ZIP はリンクカードで描く（MessageItem の文書カードと同じ判定。
  // 先頭以外の media や PubNub ファイル URL 経由で届いたときに VideoPlayer へ落とさない）
  const documentView = getChatDocumentPresentation(type);
  // URL はメッセージペイロード由来で内容を保証できないため、http(s) のみ通す
  const documentUrl = documentView ? toSafeHttpUrl(url) : undefined;
  const imageView = (
    <LazyLoadImage
      alt=""
      src={url}
      className={`${className} cursor-pointer h-full w-full object-cover`}
      height={'100%'}
      width={'100%'}
      onLoad={onLoad}
      style={style}
      // effect="blur"
    />
  );
  return (
    <>
      {documentView ? (
        documentUrl ? (
          <a
            href={documentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${className} flex items-center gap-2 px-3 py-2 text-neutral-600 hover:opacity-90`}
            style={style}
          >
            <span className="shrink-0" data-testid="document-icon">
              <Icon icon={documentView.kind} width={20} height={20} />
            </span>
            <span className="truncate underline">
              {fileName || documentUrl}
            </span>
          </a>
        ) : null
      ) : typeof type === 'string' && type.startsWith('image/') ? (
        onClick ? (
          // キーボード操作（Enter/Space）でも画像を開けるようネイティブ button で包む
          <button
            type="button"
            className="block h-full w-full"
            onClick={onClick}
            aria-label="画像を拡大表示"
          >
            {imageView}
          </button>
        ) : (
          imageView
        )
      ) : (
        <VideoPlayer
          type={type}
          url={url}
          className={className}
          isAutoPlayOnView={videoOptions.isAutoPlayVideo}
          isFullscreenOnclick={videoOptions.isFullscreenOnclick ?? true}
          controls={videoOptions.controls}
          onLoad={onLoad}
          poster={
            videoOptions.controls
              ? undefined
              : videoOptions.isAutoPlayVideo
              ? undefined
              : poster || thumbnail || ''
          }
          topOffset={topOffset}
          bottomOffset={bottomOffset}
          scrollableAncestor={scrollableAncestor}
        />
      )}
    </>
  );
};

export default memo(MediaView);
